import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signOut as firebaseSignOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';

// Create auth context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRoles, setUserRoles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Clear any existing errors
  const clearError = () => setError(null);
  
  // Check if user has admin role
  const isAdmin = () => {
    return userRoles && userRoles.admin === true;
  };
  
  // Fetch user roles from Firestore
  const fetchUserRoles = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'userRoles', uid));
      
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (err) {
      console.error('Error fetching user roles:', err);
      return null;
    }
  };
  
  // Sign in with email and password
  const signIn = async (email, password) => {
    clearError();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Fetch user roles
      const roles = await fetchUserRoles(user.uid);
      
      // If roles don't exist or user is not an admin, sign out and throw error
      if (!roles || !roles.admin) {
        await firebaseSignOut(auth);
        throw new Error('Access denied. Admin privileges required.');
      }
      
      setUserRoles(roles);
      return user;
    } catch (err) {
      if (err.message === 'Access denied. Admin privileges required.') {
        setError(err.message);
      } else {
        setError(err.message);
      }
      throw err;
    }
  };
  
  // Sign up new admin
  const signUp = async (email, password, displayName) => {
    clearError();
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Set display name if provided
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }
      
      return userCredential.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  // Sign out function
  const signOut = async () => {
    clearError();
    try {
      await firebaseSignOut(auth);
      setUserRoles(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  // Reset password
  const resetPassword = async (email) => {
    clearError();
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  // Update user profile
  const updateUserProfile = async (profileData) => {
    clearError();
    try {
      if (currentUser) {
        await updateProfile(currentUser, profileData);
        // Force refresh the currentUser object
        setCurrentUser({ ...currentUser, ...profileData });
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  useEffect(() => {
    // Firebase observer for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Fetch user roles when user signs in
        const roles = await fetchUserRoles(user.uid);
        setUserRoles(roles);
      } else {
        setUserRoles(null);
      }
      
      setCurrentUser(user);
      setLoading(false);
    });
    
    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);
  
  // Context value
  const value = {
    currentUser,
    userRoles,
    isAuthenticated: !!currentUser && isAdmin(),
    isAdmin,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateUserProfile,
    clearError
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 